'use client';
import { captureException } from '@sentry/nextjs';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import SettingConfig from '../config/setting';
import { BaseHttpResponseError, type ErrorCodeType } from '../utils/error/apiErrors';

export type QueryErrorHandler = (errorCode: ErrorCodeType | undefined, error: BaseHttpResponseError) => void;

let _errorHandler: QueryErrorHandler | undefined;

function setQueryErrorHandler(handler: QueryErrorHandler | undefined) {
  _errorHandler = handler;
}

export function useRegisterQueryErrorHandler(handler: QueryErrorHandler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    setQueryErrorHandler((errorCode, error) => handlerRef.current(errorCode, error));
    return () => setQueryErrorHandler(undefined);
  }, []);
}

export const createQueryClient = () => {
  const queryCache = new QueryCache({
    onError(error, query) {
      console.error(`Error in query ${String(query.queryKey)}:`, error);
      if (error instanceof BaseHttpResponseError) {
        _errorHandler?.(error.serverResponse.errorData?.code, error);
      }
    },
  });

  const mutationCache = new MutationCache({
    onError(error, _variables, _context, mutation) {
      // mutation 실패를 Sentry로 보낸다(중앙 1곳) — fanza 전용. 요청 컨텍스트(url/method/body)는 이미
      // client-fetch가 에러의 additionalContext에 담아두고, beforeSend가 이를 이벤트에 풀어 붙인다.
      // 중복 제거·한국어 allowlist 등 노이즈 정리는 공용 beforeSend가 담당.
      if (SettingConfig.isFanza) {
        captureException(error, {
          tags: { source: 'mutation', mutationKey: mutation.options.mutationKey?.join('/') },
        });
      }
      if (error instanceof BaseHttpResponseError) {
        _errorHandler?.(error.serverResponse.errorData?.code, error);
      }
    },
  });

  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
      },
    },
    queryCache,
    mutationCache,
  });
};

export const queryClient = createQueryClient();
